import type { LegalDocumentWords } from "./legal-words.ts";

// The Privacy Policy in English, the version that controls (ADR-024, D701, D704). Every recipient,
// field and period here was read from the code and the infrastructure, most recently on 27
// October 2026 when the retention page (ADR-026, epic 30, S30.12) folded in here instead of
// living on its own: a new provider, a new stored field or a new retention rule changes this text
// in the same release. On 30 October 2026 the visit link (ADR-028 revision 1 point 4, S33.15,
// D1117) added the line after the analytics paragraph.

export const PRIVACY_EN: LegalDocumentWords = {
  title: "Privacy Policy",
  summary:
    "What personal data {product} processes, why, for how long, who receives it, and the rights you have under the GDPR. One section is for people whose public profile {product} has read.",
  seoDescription: "How {product} collects, uses and protects your data, and the rights the GDPR gives you.",
  sections: [
    {
      id: "controller",
      heading: "1. Who is responsible",
      body: [
        "The controller is {company}, {address}. Write to {contact} about anything in this policy.",
        "We act in two roles. For your account, payments, the website and the running of {product}, we are the controller. For the lists a customer builds and the emails they send, we process data on that customer's behalf, under section 8 of our Terms of Service.",
      ],
    },
    {
      id: "website",
      heading: "2. Visiting the website",
      body: [
        "Our content delivery network and our servers process your IP address and browser details for a moment to deliver the page and keep it secure (Art. 6(1)(f) GDPR). Our request logs record the page, the time and the result, not your IP address.",
        "We store your cookie choice in your browser. We load no advertising tools.",
        "If you allow analytics in Privacy choices, the website loads PostHog to count visits. It records the pages you open and the buttons you press. It also records your browser, your device type, and the country and city your IP address points to. These are tied to a random identifier PostHog stores in your browser. What you type is never recorded. The data travels through our own address, and PostHog keeps it in the EU. You can withdraw your consent at any time in Privacy choices (Art. 6(1)(a) GDPR, § 25(1) TDDDG).",
        "If you allowed analytics and open an account from the website, we keep that random identifier with your account. It shows us which visits led to a sign-up. Without your consent, nothing is linked. It is deleted with the account (Art. 6(1)(a) GDPR).",
        "Some links carry campaign tags, which name the campaign that shared them. While your tab is open, the website keeps those tags, the site that sent you and the first page you opened. It passes them to sign-up. If you create an account, we store them with it once, to learn which campaign brought it (Art. 6(1)(f) GDPR). They are deleted with the account.",
      ],
    },
    {
      id: "account",
      heading: "3. Your account",
      body: [
        "To give you an account we process (Art. 6(1)(b) GDPR):",
        {
          list: [
            "your name, email address, password (stored only as a strong hash), language, currency and time zone, and a picture if you add one;",
            "if you sign in with Google or LinkedIn, the identifier and email address that service sends us;",
            "if you add a passkey, its public key and a label;",
            "your organizations, your role, invitations and members;",
            "your sessions: a session cookie and your browser's description, kept at most 30 days.",
          ],
        },
        "To keep {product} secure we use your IP address, shortened, to limit repeated attempts, and keep a log of important actions in your organization for 365 days (Art. 6(1)(f) GDPR).",
        "To give each person one welcome, we keep three things beside that decision (Art. 6(1)(f) GDPR): a random browser id in a cookie kept 400 days, your network block, and a hash of your email address. Your network block is your IP address cut short, never the full address.",
        "The sign-up form runs Cloudflare Turnstile, which checks that a person, not a script, fills it in. Cloudflare's Turnstile privacy addendum explains what it reads.",
        "You can erase your own account from Settings at any time. Doing so starts a 14-day grace period before anything is deleted; sign back in at any point during those 14 days and the account is restored, nothing erased. After 14 days, erasure runs and cannot be undone.",
        "If you ask our staff to erase your account instead, for example because you can no longer sign in, they confirm your email address first. They record a reason, then erase it right away, with no grace period.",
      ],
    },
    {
      id: "payments",
      heading: "4. Payments",
      body: [
        "Card payments run through Stripe and bitcoin payments through Swiss Bitcoin Pay. We never see or store your card details.",
        "We keep your billing name, email address, postal address, tax ID, invoices, credit movements and, if you tell us, why you cancelled (Art. 6(1)(b) and (c) GDPR).",
        "Invoices and credit movements are kept for 10 years, the period German tax and commercial law gives us. Once your account is erased, your name, address and tax ID are cleared from the record; the amounts, dates and invoice numbers stay.",
      ],
    },
    {
      id: "emails",
      heading: "5. Emails and notifications we send you",
      body: [
        "We send the emails the service needs, such as sign-in links, invitations, receipts and alerts (Art. 6(1)(b) GDPR).",
        "We send product news and tips only if you asked for them (Art. 6(1)(a) GDPR). Every such email has a one-click unsubscribe link.",
        "If an email to you bounces or is marked as spam, we keep your address on a list that stops further sending.",
        "If you allow push notifications, your browser's push service receives a notification address for your device. You can turn them off in your browser or in {product}.",
      ],
    },
    {
      id: "agents",
      heading: "6. AI features and agents",
      body: [
        "When an agent works on our model, the instruction and the material it needs are sent to Anthropic, which provides the model. It is not used to train the model.",
        "You may sign an agent in to your own Claude or Codex account, or store a website login in your vault. We encrypt those credentials with keys held in AWS Key Management Service. They are used only for the tasks you give.",
      ],
    },
    {
      id: "team",
      heading: "7. How our team learns about events",
      body: [
        "Our team is told in a private Slack workspace when someone signs up (name and email address), when a payment arrives, and when an email to a customer fails. This keeps support responsive (Art. 6(1)(f) GDPR).",
        "If you join the waitlist or write to us, we keep your email address and message to answer you.",
      ],
    },
    {
      id: "profiles",
      heading: "8. If {product} has read your public profile",
      body: [
        "This section is for people who never signed up, whose public Instagram profile was read at a customer's request (Art. 14 GDPR).",
        "What we read: your username, name, biography, website, and follower and following counts. Also whether the account is verified or a business, its category and its city. We read an email address or phone number only if you made it public on your profile. We may add simple derived fields, such as language or likely field of work.",
        "Where it comes from: your public profile, through a specialised data provider. We never sign in to your account or read private profiles or messages.",
        "Why: our customers look for business contacts in their market. The customer whose search found your profile is the controller of your data. They rely on a legitimate interest in finding contacts in what you chose to make public (Art. 6(1)(f) GDPR). We process it on that customer's behalf.",
        "Who receives it: only the customer whose search found your profile. That customer may email you, keep you in their own systems, or show you their ads by uploading your contact details to an ad platform. They must tell you so, and stop when you object.",
        "How long: it stays in that customer's workspace until the customer deletes it or ends their account.",
        "Your rights: you may object at any time, and ask what we hold or that we delete it. Write to {contact} with your username. We delete your profile from every list we hold. A customer who exported it before must answer for their own copy; we tell you who they are on request.",
      ],
    },
    {
      id: "recipients",
      heading: "9. Who receives data",
      body: [
        "We do not sell personal data. These providers process it for us, each only for its task:",
        {
          list: [
            "Amazon Web Services (Ireland): servers, database, backups, logs, encryption keys, and sending our emails;",
            "Cloudflare (United States): content delivery, protection of the website and app, and the sign-up check;",
            "PostHog (data kept in the EU): website analytics, only after you allow it;",
            "a specialised provider of public Instagram data: reading public profiles;",
            "Stripe (Ireland and United States): card payments;",
            "Swiss Bitcoin Pay (Switzerland): bitcoin payments;",
            "Resend (United States): sending our emails, alongside Amazon;",
            "Anthropic (United States): the model behind agents;",
            "Slack (United States): our team's notices; once a message is posted, it cannot be recalled;",
            "your browser's push service, if you allow push notifications.",
          ],
        },
        "Google and LinkedIn receive data only if you choose to sign in with them, under their own policies.",
        "We disclose data to authorities only when the law obliges us to.",
      ],
    },
    {
      id: "transfers",
      heading: "10. Transfers outside the EU",
      body: [
        "Where a provider processes data outside the EU or the EEA, we rely on an adequacy decision or on the EU Standard Contractual Clauses. Adequacy decisions include the EU-US Data Privacy Framework and the one for Switzerland.",
      ],
    },
    {
      id: "retention",
      heading: "11. How long we keep data",
      body: [
        {
          list: [
            "Account data: while the account exists, then a 14-day grace period before deletion; signing back in during those 14 days cancels it. An erasure staff carry out at your request, for example because you can no longer sign in, runs immediately, with no grace period.",
            "Sessions: at most 30 days.",
            "Organization activity log: 365 days.",
            "A record of staff actions on your account: kept indefinitely, so the action can always be checked; your name is cleared from it.",
            "Your address on our do-not-email list is kept indefinitely, even after everything else about you is erased. This follows once a message to you has bounced or you reported one as unwanted.",
            "Server logs: 30 days. Database backups: 7 days.",
            "Invoices and credit movements: 10 years.",
            "Profiles in a customer's lists: until the customer deletes them or ends their account.",
          ],
        },
      ],
    },
    {
      id: "security",
      heading: "12. Security",
      body: [
        "Data travels encrypted. The database is encrypted and not reachable from the internet. Passwords are stored only as strong hashes, and secrets in the vault are encrypted with managed keys.",
        "Only staff who need it can reach customer data. Their access is granted per person, logged, and watched by alarms.",
      ],
    },
    {
      id: "rights",
      heading: "13. Your rights",
      body: [
        "You may ask for access to your data, its correction or deletion, a restriction of its use, or a copy to take elsewhere. You may object to processing based on legitimate interest, and withdraw a consent at any time without affecting what happened before.",
        "Write to {contact}. We answer within one month.",
        "You may also complain to a data protection authority. Ours is the {authority}.",
        "We make no decision about you that has legal or similarly significant effects by automated means alone. Lead scores help our customers sort their own lists.",
      ],
    },
    {
      id: "changes",
      heading: "14. Changes",
      body: [
        "We update this policy when what we process changes. The date at the top shows the current version; we email account holders about material changes.",
      ],
    },
  ],
};
